import { FC, HTMLAttributes, PropsWithChildren, useState } from 'react';
import { classNames } from 'utils/classNames';
import {
	ESchemaComponentType,
	IHomeDnDContentElement,
	ISchemaComponentGenericSwimlane,
	ISchemaComponentHeroSwimlane,
	ISchemaComponentMatrixVendorSwimlane,
	ISchemaComponentWideVendorSwimlane,
} from './types';
import styles from './SchemaComponent.module.css';
import { Flex } from 'antd';
import noImgLogo from 'assets/images/no-image.svg';

const SchemaComponentImage: FC<PropsWithChildren & HTMLAttributes<HTMLElement> & { image: string | undefined }> = ({
	image,
	...props
}) => {
	const [imageHasError, setImageHasError] = useState<boolean>(false);
	return (
		<img
			alt=''
			src={imageHasError ? noImgLogo : image}
			style={{
				objectFit: 'contain',
				opacity: image ? 'unset' : 0.5,
			}}
			onError={() => setImageHasError(true)}
			{...props}
		/>
	);
};

export const CONTENT_RENDERS: Record<ESchemaComponentType, FC<undefined | IHomeDnDContentElement>> = {
	[ESchemaComponentType.MATRIX_VENDOR_SWIMLANE]: (props) => {
		const contentConfigs = props?.component as ISchemaComponentMatrixVendorSwimlane | undefined;

		const elementsPerColumn = 2;

		const Column = ({ columnIndex }: { columnIndex: number }) => (
			<Flex
				gap='small'
				justify='center'
				vertical
			>
				{[...Array(elementsPerColumn)].map((_, index) => {
					const vendorIndex = columnIndex * elementsPerColumn + index;
					const vendor = contentConfigs?.vendors?.at(vendorIndex);

					return (
						<Flex
							key={vendorIndex}
							gap='small'
						>
							<SchemaComponentImage
								image={vendor?.image}
								className={classNames(styles.template_box_component, styles.small)}
							/>
							____
						</Flex>
					);
				})}
			</Flex>
		);

		return (
			<Flex
				gap='small'
				justify='center'
				className={styles.template_matrix_vendor_swimlane}
			>
				{[...Array(3)].map((_, index) => (
					<Column
						key={index}
						columnIndex={index}
					/>
				))}
			</Flex>
		);
	},

	[ESchemaComponentType.HERO_VENDOR_SWIMLANE]: (props) => {
		const contentConfigs = props?.component as ISchemaComponentHeroSwimlane | undefined;

		return (
			<Flex
				gap='small'
				vertical
				justify='center'
				className={classNames(styles.template_hero_vendor_swimlane)}
			>
				<div>{contentConfigs?.title ?? '__ ___ _ _ __'}</div>
				<Flex
					gap='small'
					justify='center'
				>
					{[...Array(3)].map((_, index) => {
						const vendor = contentConfigs?.vendors?.at(index);

						return (
							<div
								key={index}
								className='flex-one'
							>
								<SchemaComponentImage
									image={vendor?.image}
									className={styles.template_box_component}
								/>
								<div className={styles.text}>____</div>
							</div>
						);
					})}
				</Flex>
			</Flex>
		);
	},
	[ESchemaComponentType.GENERIC_VENDOR_SWIMLANE]: (props) => {
		const contentConfigs = props?.component as ISchemaComponentGenericSwimlane | undefined;

		return (
			<Flex
				vertical
				gap='small'
				className={styles.template_generic_vendor_swimlane}
			>
				<Flex
					gap='small'
					align='center'
				>
					<SchemaComponentImage
						image={contentConfigs?.image}
						className={classNames(
							styles.logo,
							styles.template_box_component,
							styles.template_box_component_circle
						)}
					/>
					<div className={styles.title}>{contentConfigs?.title ?? '__ ___ _ _ __'}</div>
				</Flex>
				<Flex
					gap='small'
					className={styles.list}
				>
					{[...Array(8)].map((_, index) => {
						const vendor = contentConfigs?.vendors?.at(index);

						return (
							<div
								key={index}
								className={styles.list_item}
							>
								<SchemaComponentImage
									image={vendor?.image}
									className={styles.template_box_component}
								/>
								<div className={styles.text}>____</div>
							</div>
						);
					})}
				</Flex>
			</Flex>
		);
	},

	[ESchemaComponentType.WIDE_VENDOR_SWIMLANE]: (props) => {
		const contentConfigs = props?.component as ISchemaComponentWideVendorSwimlane;

		return <>{contentConfigs.name}</>;
	},
};
